import "./shared/base.css";
import "./shared/tokens.css";
import "./shared/fonts.css";
import "./components/index.js";
import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Router } from "@lit-labs/router";
import { routes, fallback } from "./router.js";
import { bindRouter, navigate } from "./nav.js";
import { pb } from "./api/client.js";
import { signOut, isOperator } from "./api/auth.js";
import { parseError } from "./api/errors.js";

interface NavItem {
  href: string;
  label: string;
  /** Prefixes (besides the exact href) that also mark this item active. */
  match?: string[];
}

const NAV: NavItem[] = [
  { href: "/workspaces", label: "Workspaces", match: ["/workspaces", "/builds", "/"] },
  { href: "/teams", label: "Teams", match: ["/teams"] },
  { href: "/api-keys", label: "API", match: ["/api-keys"] },
  { href: "/usage", label: "Usage", match: ["/usage"] },
  { href: "/security", label: "Security", match: ["/security", "/account"] },
];

// Appended for operators only (the authStore re-render picks up flag changes
// after an authRefresh). The route behind it is guarded independently.
const ADMIN_NAV: NavItem = { href: "/admin", label: "Admin", match: ["/admin"] };

/**
 * bino-cloud-app is the application shell: a sticky top bar (brand mark, primary
 * nav, and the signed-in identity + sign-out) and the routed outlet. It owns the
 * single top-level Router, which intercepts in-app link clicks and browser
 * navigation and re-renders the outlet — and this shell — on every route change,
 * so the active nav item and auth state derive from location at render time. It
 * subscribes to the PocketBase authStore so signing in/out re-renders the chrome;
 * unauthenticated users are redirected to /login by the per-route guard.
 */
@customElement("bino-cloud-app")
export class BinoCloudApp extends LitElement {
  static override styles = css`
    :host {
      display: block;
      min-height: 100vh;
      background: var(--bino-bg);
      color: var(--bino-text);
      font-family: var(--bino-font-sans);
    }
    header {
      position: sticky;
      top: 0;
      z-index: var(--bino-z-toolbar);
      background: var(--bino-surface);
      border-bottom: 1px solid var(--bino-border);
      box-shadow: var(--bino-shadow-header);
    }
    .bar {
      display: flex;
      align-items: center;
      gap: var(--bino-space-lg);
      height: 54px;
      max-width: 1240px;
      margin: 0 auto;
      padding: 0 var(--bino-space-lg);
    }
    .brand {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      text-decoration: none;
    }
    .glyph {
      width: 24px;
      height: 24px;
      border-radius: 7px;
      background: var(--bino-primary);
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      font: 800 13px/1 var(--bino-font-sans);
    }
    .brand-label {
      font: 600 15px/1 var(--bino-font-sans);
      color: var(--bino-text);
      white-space: nowrap;
    }
    nav.tabs {
      display: flex;
      align-items: center;
      gap: 2px;
    }
    .tab {
      font: 500 13px/1 var(--bino-font-sans);
      color: var(--bino-text-secondary);
      padding: 7px 11px;
      border-radius: var(--bino-radius-sm);
      text-decoration: none;
      white-space: nowrap;
      transition:
        background var(--bino-transition-fast),
        color var(--bino-transition-fast);
    }
    .tab:hover {
      color: var(--bino-text);
      background: var(--bino-surface-hover);
    }
    .tab.active {
      color: var(--bino-active-text);
      font-weight: 600;
      background: var(--bino-surface-active);
    }
    .spacer {
      flex: 1;
    }
    .who {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
    }
    .email {
      font: 500 12.5px/1 var(--bino-font-mono);
      color: var(--bino-text-secondary);
      max-width: 18rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    main {
      max-width: 1240px;
      margin: 0 auto;
      padding: var(--bino-space-xl) var(--bino-space-lg) 5rem;
    }
    @media (max-width: 720px) {
      .bar {
        flex-wrap: wrap;
        gap: var(--bino-space-md);
        row-gap: 0;
        height: auto;
        min-height: 54px;
        padding: 6px var(--bino-space-md);
      }
      /* Nav drops to a full-width second row and pans horizontally instead of
         widening the document; brand, spacer, and sign-out stay on row one. */
      nav.tabs {
        order: 1;
        flex-basis: 100%;
        min-width: 0;
        overflow-x: auto;
        scrollbar-width: none;
      }
      nav.tabs::-webkit-scrollbar {
        display: none;
      }
      .email {
        display: none;
      }
    }
  `;

  private router = new Router(this, routes, { fallback });
  @state() private authVersion = 0;
  private unsub?: () => void;

  override connectedCallback() {
    super.connectedCallback();
    bindRouter(this.router);
    this.unsub = pb.authStore.onChange(() => (this.authVersion += 1));
    void this.slideSession();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.unsub?.();
  }

  /**
   * slideSession renews the session token on every app boot. Auth tokens are
   * deliberately short-lived (24h), so a leaked one stops working quickly — but a
   * fixed TTL would also expire under an active user mid-session. Refreshing at
   * boot slides the window forward: anyone who opens the app at least once a day
   * never sees the expiry, while an idle-for-a-day session is genuinely gone.
   *
   * A 401/403 means the server rejected the token — expired, or revoked by
   * sign-out-everywhere from another device. Clearing the store and re-entering
   * the current route lets the auth guard bounce to /login (preserving ?next=)
   * instead of leaving the SPA rendering chrome for a session that is dead. Other
   * failures (offline, server hiccup) are left alone: the stored token may still
   * be perfectly valid, and signing the user out over a flaky network is worse
   * than letting the next real request surface the error.
   */
  private async slideSession() {
    if (!pb.authStore.isValid) return;
    try {
      await pb.collection("users").authRefresh();
    } catch (err) {
      const { status } = parseError(err);
      if (status !== 401 && status !== 403) return;
      signOut();
      void this.router.goto(location.pathname);
    }
  }

  private isActive(item: NavItem): boolean {
    const p = location.pathname;
    const prefixes = item.match ?? [item.href];
    return prefixes.some((m) => (m === "/" ? p === "/" : p === m || p.startsWith(m + "/")));
  }

  private onSignOut() {
    signOut();
    navigate("/login");
  }

  override render() {
    const authed = pb.authStore.isValid;
    const email = (pb.authStore.record?.email as string | undefined) ?? "";
    const nav = isOperator() ? [...NAV, ADMIN_NAV] : NAV;
    return html`
      <header>
        <div class="bar">
          <a class="brand" href="/" aria-label="bino Cloud home">
            <span class="glyph" aria-hidden="true">b</span>
            <span class="brand-label">bino Cloud</span>
          </a>
          ${authed
            ? html`<nav class="tabs" aria-label="Primary">
                ${nav.map(
                  (item) =>
                    html`<a class="tab ${this.isActive(item) ? "active" : ""}" href=${item.href}
                      >${item.label}</a
                    >`,
                )}
              </nav>`
            : ""}
          <span class="spacer"></span>
          ${authed
            ? html`<div class="who">
                <span class="email" title=${email}>${email}</span>
                <bn-button variant="ghost" size="sm" @click=${this.onSignOut}>Sign out</bn-button>
              </div>`
            : html`<a class="tab" href="/login">Sign in</a>`}
        </div>
      </header>
      <main>${this.router.outlet()}</main>
      <bn-toaster></bn-toaster>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bino-cloud-app": BinoCloudApp;
  }
}
